<template>
  <div class="space-y-6">
    <!-- Prefill Button -->
    <div class="flex justify-end">
      <button
        type="button"
        class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md
          text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        @click="$emit('prefill')">
        <i class="fas fa-magic mr-2" />
        Prefill from Website Info
      </button>
    </div>

    <!-- Location Selection -->
    <div>
      <label
        for="location"
        class="block text-sm font-medium text-gray-700">
        Location <span class="text-red-500">*</span>
      </label>
      <select
        id="location"
        v-model="localData.location_id"
        class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none
          focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
        @change="updateData">
        <option value="">
          Select a location...
        </option>
        <option
          v-for="location in locations"
          :key="location.id"
          :value="location.id">
          {{ location.name }}
        </option>
      </select>
    </div>

    <!-- Website URL -->
    <div>
      <label
        for="website"
        class="block text-sm font-medium text-gray-700">
        Website URL
      </label>
      <div class="mt-1">
        <input
          id="website"
          v-model="localData.website"
          type="url"
          placeholder="https://example.com"
          class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          @input="updateData">
      </div>
    </div>

    <!-- Description -->
    <div>
      <label
        for="description"
        class="block text-sm font-medium text-gray-700">
        Business Description
      </label>
      <div class="mt-1">
        <textarea
          id="description"
          v-model="localData.description"
          rows="3"
          class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          placeholder="Describe the business and its services..."
          @input="updateData" />
      </div>
      <p class="mt-2 text-sm text-gray-500">
        This description will be used for the Google Business Profile
      </p>
    </div>

    <!-- Category -->
    <div>
      <label
        for="category"
        class="block text-sm font-medium text-gray-700">
        Business Category
      </label>
      <div class="mt-1">
        <input
          id="category"
          v-model="localData.category"
          type="text"
          placeholder="e.g., Restaurant, Plumber, Law Firm"
          class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          @input="updateData">
      </div>
      <p class="mt-2 text-sm text-gray-500">
        Primary Google Business category
      </p>
    </div>

    <!-- Keywords -->
    <div>
      <label
        for="keywords"
        class="block text-sm font-medium text-gray-700">
        Target Keywords
      </label>
      <div class="mt-1">
        <input
          id="keywords"
          v-model="keywordsString"
          type="text"
          placeholder="keyword1, keyword2, keyword3"
          class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          @blur="updateKeywords"
          @change="updateKeywords">
      </div>
      <p class="mt-2 text-sm text-gray-500">
        Comma-separated list of keywords to target
      </p>
    </div>

    <!-- Avoid Keywords -->
    <div>
      <label
        for="avoid_keywords"
        class="block text-sm font-medium text-gray-700">
        Keywords to Avoid
      </label>
      <div class="mt-1">
        <input
          id="avoid_keywords"
          v-model="avoidKeywordsString"
          type="text"
          placeholder="spam1, spam2, spam3"
          class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          @blur="updateAvoidKeywords"
          @change="updateAvoidKeywords">
      </div>
      <p class="mt-2 text-sm text-gray-500">
        Comma-separated list of keywords that violate Google's terms
      </p>
    </div>

    <!-- Information Panel -->
    <div class="rounded-md bg-blue-50 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <i class="fas fa-info-circle text-blue-400" />
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-blue-800">
            About GBP Checklists
          </h3>
          <div class="mt-2 text-sm text-blue-700">
            <p>
              This checklist helps ensure Google Business Profiles are properly optimized.
              Fill in the basic information, then proceed through each tab to complete the audit.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  locations: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'prefill'])

// Local data copy
const localData = ref({ ...props.modelValue })

// String representations of keywords for input fields
const keywordsString = ref((props.modelValue.keywords || []).join(', '))
const avoidKeywordsString = ref((props.modelValue.avoid_keywords || []).join(', '))

// Methods
const updateData = () => {
  emit('update:modelValue', { ...localData.value })
}

const updateKeywords = () => {
  // Parse keywords on blur/change
  localData.value.keywords = keywordsString.value
    .split(',')
    .map((k) => k.trim())
    .filter((k) => k)
  updateData()
}

const updateAvoidKeywords = () => {
  // Parse avoid keywords on blur/change
  localData.value.avoid_keywords = avoidKeywordsString.value
    .split(',')
    .map((k) => k.trim())
    .filter((k) => k)
  updateData()
}

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  localData.value = { ...newValue }
  // Update keyword strings when props change
  keywordsString.value = (newValue.keywords || []).join(', ')
  avoidKeywordsString.value = (newValue.avoid_keywords || []).join(', ')
}, { deep: true })
</script>
